import { fetchBatchWidgetData, buildWidgetFilters } from "./AnalyticsService";
import { inboundCallsDashboard } from "../Config/dashboardConfigs";
import { setAnalyticsPrefetch } from "../Redux/Actions/AnalyticsActions";

export const PREFETCH_TTL_MS = 5 * 60 * 1000; // 5 minutes
export const PREFETCH_AWAIT_MAX_MS = 1500; // wait at most 1.5s for an in-flight prefetch
const PREFETCH_MAX_CONCURRENT = 2; // backend Cosmos throttles if > 2 batches in parallel

// Suivi des prefetch en cours: { [scope]: Promise<results> }
// Permet au dashboard d'attendre une promise déjà en vol au lieu de relancer un appel.
const inFlight = new Map();

// File d'attente pour throttle: scopes qui attendent qu'un slot se libère
const queue = [];
let activeCount = 0;

const runNext = () => {
    if (activeCount >= PREFETCH_MAX_CONCURRENT || queue.length === 0) return;
    const job = queue.shift();
    activeCount++;
    job().finally(() => {
        activeCount--;
        runNext();
    });
};

const buildDefaultFilters = (scope) => {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date();
    endDate.setHours(0, 0, 0, 0);
    return {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        scope,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
};

const buildPayload = (scope, profile) => {
    const baseFilters = buildDefaultFilters(scope);
    const apiWidgets = inboundCallsDashboard.widgets.filter(
        (w) => (!w.config?.dataSource || w.config?.dataSource !== "reportConfig") && w.queryId
    );

    return apiWidgets.map((w) => {
        let enriched = buildWidgetFilters(w, baseFilters);
        const t = profile?.reportConfig?.thresholds;
        if (t) {
            enriched = {
                ...enriched,
                quickAnswerThreshold: t.quickAnswerThreshold,
                standardAnswerThreshold: t.standardAnswerThreshold,
                delayedAnswerThreshold: t.delayedAnswerThreshold,
                quickDropThreshold: t.quickDropThreshold,
                // Seuil SLA en secondes — un seul, partagé par toutes les requêtes.
                slaSeconds: t.slaSeconds,
            };
        }
        return { widgetId: w.id, queryId: w.queryId, widgetType: w.type, filters: enriched };
    });
};

export const prefetchScopeWidgets = ({ scope, tenantId, token, profile, dispatch }) => {
    if (!scope || !tenantId || !token) return null;
    if (inFlight.has(scope)) return inFlight.get(scope);

    const batchPayload = buildPayload(scope, profile);
    if (batchPayload.length === 0) return null;

    console.log(`📋 [prefetch] queue "${scope}" (${batchPayload.length} widgets)`);

    // Wrapper la promise dans le throttle: le job ne démarre que quand un slot se libère.
    let resolveJob;
    const promise = new Promise((resolve) => { resolveJob = resolve; });

    const job = () => {
        const t0 = performance.now();
        console.log(`📡 [prefetch] start "${scope}"`);
        return fetchBatchWidgetData(tenantId, batchPayload, token).then((response) => {
            const ms = Math.round(performance.now() - t0);
            inFlight.delete(scope);
            if (!response.success) {
                console.warn(`⚠️ [prefetch] "${scope}" failed in ${ms}ms: ${response.error}`);
                resolveJob(null);
                return;
            }
            console.log(`✅ [prefetch] "${scope}" done in ${ms}ms (${response.results.length} widgets)`);
            const payload = { scope, results: response.results, batchPayload };
            dispatch(setAnalyticsPrefetch(payload));
            resolveJob(payload);
        }).catch((err) => {
            inFlight.delete(scope);
            console.warn(`⚠️ [prefetch] "${scope}" threw:`, err);
            resolveJob(null);
        });
    };

    inFlight.set(scope, promise);
    queue.push(job);
    runNext();
    return promise;
};

export const getInFlightPrefetch = (scope) => inFlight.get(scope) || null;

export const prefetchAllScopes = ({ scopeNames, analyticsProfiles, tenantId, token, dispatch, prefetchState }) => {
    if (!scopeNames || !tenantId || !token) return;
    const profilesByScope = {};
    if (analyticsProfiles) {
        Object.values(analyticsProfiles).forEach((p) => {
            if (p?.scope) profilesByScope[p.scope] = p;
        });
    }
    const defaultProfile = profilesByScope.default;
    const now = Date.now();

    scopeNames.forEach((scope) => {
        const cached = prefetchState?.[scope];
        if (cached && now - cached.timestamp < PREFETCH_TTL_MS) return;
        const profile = profilesByScope[scope] || defaultProfile;
        prefetchScopeWidgets({ scope, tenantId, token, profile, dispatch });
    });
};
